import axios from 'axios';
import { auth } from '../config/firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  updateProfile: (updates) => api.patch('/auth/me', updates),
  getUserByUsername: (username) => api.get(`/auth/user/${username}`),
  changeUsername: (newUsername) => api.post('/auth/change-username', { newUsername }),
};

// Prompts API
export const promptsAPI = {
  getAll: (params) => api.get('/prompts', { params }),
  getById: (id) => api.get(`/prompts/${id}`),
  create: (promptData) => api.post('/prompts', promptData),
  update: (id, updates) => api.patch(`/prompts/${id}`, updates),
  delete: (id) => api.delete(`/prompts/${id}`),
  fork: (id, modifications) => api.post(`/prompts/${id}/fork`, { modifications }),
  getWeeklyTop: (limit = 10) => api.get('/prompts/leaderboard/weekly', { params: { limit } }),
  getFeatured: (limit = 5) => api.get('/prompts/featured/list', { params: { limit } }),
  getUserPrompts: (userId, params) => api.get(`/prompts/user/${userId}`, { params }),
};

// Ratings API
export const ratingsAPI = {
  getByPromptId: (promptId, params) => api.get(`/ratings/prompt/${promptId}`, { params }),
  getStats: (promptId) => api.get(`/ratings/prompt/${promptId}/stats`),
  getMyRating: (promptId) => api.get(`/ratings/prompt/${promptId}/my-rating`),
  create: (ratingData) => api.post('/ratings', ratingData),
  update: (id, updates) => api.patch(`/ratings/${id}`, updates),
  delete: (id) => api.delete(`/ratings/${id}`),
  markHelpful: (id) => api.post(`/ratings/${id}/helpful`),
};

// Token Analyzer API
export const tokenAnalyzerAPI = {
  count: (promptText, modelUsed) => api.post('/token-analyzer/count', { promptText, modelUsed }),
  analyze: (promptText, modelUsed) => api.post('/token-analyzer/analyze', { promptText, modelUsed }),
  optimize: (promptText, modelUsed) => api.post('/token-analyzer/optimize', { promptText, modelUsed }),
  compare: (promptText) => api.post('/token-analyzer/compare', { promptText }),
  getHistory: (params) => api.get('/token-analyzer/history', { params }),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
};

// Leaderboard API
export const leaderboardAPI = {
  get: (params) => api.get('/leaderboard', { params }),
};

// Gamification API
export const gamificationAPI = {
  getSummary: (userId) => api.get(`/gamification/${userId}`),
};

// Comments API
export const commentsAPI = {
  getByPromptId: (promptId) => api.get(`/comments/prompt/${promptId}`),
  getReplies: (commentId) => api.get(`/comments/${commentId}/replies`),
  create: (data) => api.post('/comments', data),
  update: (commentId, data) => api.patch(`/comments/${commentId}`, data),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
  markHelpful: (commentId) => api.post(`/comments/${commentId}/helpful`),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Follows API
export const followsAPI = {
  follow: (userId) => api.post(`/follows/${userId}`),
  unfollow: (userId) => api.delete(`/follows/${userId}`),
  getStatus: (userId) => api.get(`/follows/${userId}/status`),
  getFollowing: (userId, params) => api.get(`/follows/${userId}/following`, { params }),
  getFollowers: (userId, params) => api.get(`/follows/${userId}/followers`, { params }),
  getCounts: (userId) => api.get(`/follows/${userId}/counts`),
  getMutual: (userId) => api.get(`/follows/${userId}/mutual`),
  getSuggestions: (params) => api.get('/follows/suggestions', { params }),
};

// Auto-prompts API (bot ingestion + claim)
export const autoPromptsAPI = {
  ingest: (data, token) => api.post('/auto-prompts/ingest', data, {
    headers: { 'x-autobot-token': token },
  }),
  publish: (id, payload, token) => api.post(`/auto-prompts/${id}/publish`, payload || {}, {
    headers: { 'x-autobot-token': token },
  }),
  claimWithToken: (token) => api.post(`/auto-prompts/claim/${token}`),
  claimByPrompt: (promptId) => api.post(`/auto-prompts/claim/by-prompt/${promptId}`),
};

// Admin API
export const adminAPI = {
  getPendingClaims: () => api.get('/admin/claims/pending'),
  approveClaim: (id) => api.post(`/admin/claims/${id}/approve`),
  rejectClaim: (id) => api.post(`/admin/claims/${id}/reject`),
  getPendingPrompts: (params) => api.get('/admin/prompts/pending', { params }),
  searchPrompts: (params) => api.get('/admin/prompts/search', { params }),
  updatePrompt: (id, payload) => api.patch(`/admin/prompts/${id}`, payload),
  transferPromptOwner: (id, payload) => api.post(`/admin/prompts/${id}/transfer`, payload),
  bulkTransferPrompts: (ids, payload) => api.post('/admin/prompts/bulk/transfer', { ids, ...(payload || {}) }),
  bulkDeletePrompts: (ids) => api.post('/admin/prompts/bulk/delete', { ids }),
  updatePromptStatus: (id, payload) => api.patch(`/admin/prompts/${id}/status`, payload),
  updateUserStatus: (id, account_status) => api.patch(`/admin/users/${id}/status`, { account_status }),
  getAutoPrompts: (params) => api.get('/admin/auto-prompts', { params }),
  publishAutoPrompt: (id) => api.post(`/admin/auto-prompts/${id}/publish`),
  deleteAutoPrompt: (id) => api.delete(`/admin/auto-prompts/${id}`),
  bulkPublishAutoPrompts: (ids, payload) => api.post('/admin/auto-prompts/bulk/publish', { ids, ...(payload || {}) }),
  bulkDeleteAutoPrompts: (ids) => api.post('/admin/auto-prompts/bulk/delete', { ids }),
};

// Activity Feed API
export const activityAPI = {
  getFeed: (params) => api.get('/activity/feed', { params }),
  getUserActivity: (userId, params) => api.get(`/activity/user/${userId}`, { params }),
  getGlobal: (params) => api.get('/activity/global', { params }),
};

// Search API
export const searchAPI = {
  prompts: (params) => api.get('/search/prompts', { params }),
  users: (params) => api.get('/search/users', { params }),
  suggestions: (params) => api.get('/search/suggestions', { params }),
};

// Collections API
export const collectionsAPI = {
  getAll: (params) => api.get('/collections', { params }),
  getPublic: (params) => api.get('/collections/public', { params }),
  getById: (id, params) => api.get(`/collections/${id}`, { params }),
  create: (data) => api.post('/collections', data),
  update: (id, data) => api.patch(`/collections/${id}`, data),
  delete: (id) => api.delete(`/collections/${id}`),
  addPrompt: (collectionId, promptId) => api.post(`/collections/${collectionId}/prompts/${promptId}`),
  removePrompt: (collectionId, promptId) => api.delete(`/collections/${collectionId}/prompts/${promptId}`),
  getCollectionsForPrompt: (promptId) => api.get(`/collections/prompt/${promptId}`),
};

// Reports API
export const reportsAPI = {
  report: (promptId, data) => api.post(`/reports/prompt/${promptId}`, data),
  getMyReports: (params) => api.get('/reports/my-reports', { params }),
  getPromptReports: (promptId) => api.get(`/reports/prompt/${promptId}`), // Admin only
  getStats: () => api.get('/reports/stats'), // Admin only
  dismissReports: (promptId) => api.post(`/admin/reports/${promptId}/dismiss`), // Admin only
  banPrompt: (promptId) => api.post(`/admin/reports/${promptId}/ban`), // Admin only
  getAllReported: (params) => api.get('/admin/reports', { params }), // Admin only
};

// AI Agents API
export const agentsAPI = {
  getAll: (params) => api.get('/agents', { params }),
  getById: (id) => api.get(`/agents/${id}`),
  getUserAgents: (userId, params) => api.get(`/agents/user/${userId}`, { params }),
  create: (data) => api.post('/agents', data),
  update: (id, data) => api.patch(`/agents/${id}`, data),
  delete: (id) => api.delete(`/agents/${id}`),
  fork: (id) => api.post(`/agents/${id}/fork`),
  getRelated: (id, params) => api.get(`/agents/${id}/related`, { params }),
};

// MCP Servers API
export const mcpServersAPI = {
  getAll: (params) => api.get('/mcp-servers', { params }),
  getById: (id) => api.get(`/mcp-servers/${id}`),
  getUserServers: (userId, params) => api.get(`/mcp-servers/user/${userId}`, { params }),
  create: (data) => api.post('/mcp-servers', data),
  update: (id, data) => api.patch(`/mcp-servers/${id}`, data),
  delete: (id) => api.delete(`/mcp-servers/${id}`),
  clone: (id) => api.post(`/mcp-servers/${id}/clone`),
  trackClone: (id) => api.post(`/mcp-servers/${id}/track-clone`),
  getRelated: (id, params) => api.get(`/mcp-servers/${id}/related`, { params }),
  getByClient: (clientName, params) => api.get(`/mcp-servers/client/${clientName}`, { params }),
};

export default api;
